import { useState } from 'react';
import { useNavigate, Link, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';
import {
  getRememberLoginPreference,
  setRememberLoginPreference,
  getSavedLoginEmail,
  setSavedLoginEmail,
  clearSavedLoginEmail,
} from '../utils/rememberLogin';

function postAuthRedirectPath(searchParams) {
  const raw = searchParams.get('next') || searchParams.get('returnTo');
  if (raw && raw.startsWith('/') && !raw.startsWith('//')) return raw;
  return '/app/dashboard';
}

export default function LoginPage() {
  const { login, user, sessionReady, error: authError, setError: setAuthError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(() => getSavedLoginEmail());
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(() => getRememberLoginPreference());
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const displayError = error || authError || '';

  if (!sessionReady) {
    return (
      <div className="app-auth-page app-dashboard-backdrop">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Loading…</p>
      </div>
    );
  }

  if (user) {
    return <Navigate to={postAuthRedirectPath(searchParams)} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    const emailClean = String(email || '').trim().toLowerCase();
    if (!emailClean) {
      setError('Enter a valid email address.');
      return;
    }
    setError('');
    setAuthError(null);
    setLoading(true);
    try {
      const inviteToken =
        searchParams.get('invite') || searchParams.get('token') || undefined;
      await login(emailClean, password, inviteToken, rememberMe);
      setRememberLoginPreference(rememberMe);
      if (rememberMe) setSavedLoginEmail(emailClean);
      else clearSavedLoginEmail();
      navigate(postAuthRedirectPath(searchParams), { replace: true });
    } catch (err) {
      setError(err?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-auth-page app-dashboard-backdrop px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center" aria-label="Stavyn">
            <Logo style={{ height: 36, width: 'auto' }} />
          </Link>
        </div>

        <div className="app-auth-card">
          <h1 className="app-auth-card__title">Welcome back</h1>
          <p className="app-auth-card__subtitle">Sign in to your Stavyn dashboard</p>

          {displayError && (
            <div className="app-auth-error">{displayError}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="app-auth-label" htmlFor="login-email">Email address</label>
              <input
                id="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="app-input"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="app-auth-label" htmlFor="login-password">Password</label>
              <input
                id="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="app-input"
                placeholder="••••••••"
              />
            </div>
            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border"
                style={{ borderColor: 'var(--border-main)', accentColor: 'var(--app-accent-brand)' }}
              />
              <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Keep me signed in on this device</span>
            </label>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center"
              style={{ display: 'flex' }}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <p className="text-center text-sm mt-4" style={{ color: 'var(--text-muted)' }}>
            New to Stavyn?{' '}
            {(searchParams.get('invite') || searchParams.get('token')) ? (
              <Link
                to={`/signup?${searchParams.toString()}`}
                className="app-auth-link"
              >
                Create your account with this invitation
              </Link>
            ) : (
              'Use the invitation link from your administrator to create an account.'
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
